<template>
  <v-container class="pa-0" fluid>
    <app-loading :loading="loading">
      <div v-if="data.speaks && data.speaks[current]">
        <app-header :button="false" :numbers="data.speaks.length > 1">
          <template #title>{{ data.speaks[current].title }}</template>
          <template #subtitle>{{ data.speaks[current].subtitle }}</template>
          <template #current-step>{{ current + 1 }}</template>
          <template #max-step>{{ data.speaks.length }}</template>
        </app-header>
        <v-row class="pa-4" justify="end">
          <v-btn
            color="#29284c"
            small
            outlined
            width="120"
            class="text-capitalize"
            @click="backAction"
            >Previous Step</v-btn
          >
          <v-btn
            @click="next"
            color="#FF347C"
            width="120"
            small
            depressed
            class="white--text text-capitalize ml-2"
            >{{ current === data.speaks.length - 1 ? "Finish" : "Next >>" }}</v-btn
          >
        </v-row>
        <app-explanation>
          <template #body>
            <span class="title">{{ data.speaks[current].description }}</span>
          </template>
        </app-explanation>
        <template v-if="data.speaks[current]">
          <app-comment
            :audioSrc="data.speaks[current].question_group.questions[0].question_file"
            class="mt-5"
            :body="JSON.parse(data.speaks[current].question_group.questions[0].content)"
          >
            <template #title>Speaking Prompts</template>
          </app-comment>
          <app-speaking
            class="mt-5"
            ref="speaking"
            :answer="answers[current]"
            :commentDialog="commentDialog"
          />
          <div class="d-flex justify-center mt-6">
            <v-btn
              v-if="!(!answers[current] || answers[current].uuid)"
              small
              depressed
              width="100"
              :loading="loadingSave"
              class="white--text text-capitalize"
              color="#29284c"
              @click="onSave"
            >
              Save
            </v-btn>
            <ProgressResetButton
              v-else
              :stepType="direction === 'teach' ? 'TEACH_SPEAK' : 'PRACTICE_SPEAK'"
              :stepUuid="speak"
              @reset="onReset"
            />
          </div>
          <app-comment-group
            onlyOne
            ref="commentGroup"
            class="mt-5"
            :uuid="data.speaks[current].comment_group"
            @comment-dialog="commentDialog = $event"
          >
            <template #reassign>
              <v-btn
                v-show="isTeacher && !homeworks.length"
                class="text-capitalize comment__div custom-text"
                color="white"
                @click="createDialog = true"
                >Reassign
                <v-icon right> mdi-repeat </v-icon>
              </v-btn>
              <CreateHomeworkDialog
                v-model="createDialog"
                max-width="800"
                :location="location"
                :loadingAssign="loadingAssign"
                :createFields="createFields"
                defaultText="Instructions here..."
                @new-homework="onNewHomework"
              >
                <template #title>
                  <span class="font-weight-bold">Do it again!</span>
                </template>
              </CreateHomeworkDialog>
            </template>
            <template #reassign-tasks>
              <v-row class="mt-4">
                <HomeworkDetails
                  v-for="(item, index) in homeworks"
                  :key="item.uuid"
                  :item="item"
                  :index="index"
                  @homework-deleted="onDeleted"
                >
                  <template #title>
                    <span>Do it again!</span>
                  </template>
                </HomeworkDetails>
              </v-row>
            </template>
          </app-comment-group>
        </template>
      </div>
    </app-loading>
    <app-student-note v-if="!isTeacher && qualification.note" :note="qualification.note" />
    <app-qualification
      v-if="isTeacher && !qualification.note && qualification.uuid"
      :qualification="qualification"
      @new-qual="qualification = $event"
    />
    <app-qualification-dialog
      v-model="dialog"
      :qualification="qualification"
      @new-qual="qualification = $event"
      @input="onInput"
    />
  </v-container>
</template>
<script>
import Header from "@/components/Lessons/Lesson/components/Header.vue";
import Explanation from "@/components/Lessons/Lesson/components/Explanation.vue";
import Comment from "@/components/Lessons/Lesson/components/Comment.vue";
import CommentGroup from "@/components/Lessons/Lesson/components/CommentGroup.vue";
import Speaking from "@/components/Lessons/Lesson/components/Speaking.vue";
import {
  assignHomework,
  getAnswer,
  getHomeworks,
  getSpeaking,
  postAnswer,
  patchLessonProgress,
} from "@/api/api";
import Loading from "@/components/Loading.vue";
import directionMixin from "@/mixins/directionMixin";
import studentMixin from "@/mixins/studentMixin";
import Qualification from "@/components/Utils/Qualification.vue";
import QualificationDialog from "@/components/Utils/QualificationDialog.vue";
import StudentNote from "@/components/Utils/StudentNote.vue";
import HomeworkDetails from "./Homework/HomeworkDetails.vue";
import CreateHomeworkDialog from "./Homework/CreateHomeworkDialog.vue";
import ProgressResetButton from "@/components/Utils/ProgressResetButton.vue";

export default {
  components: {
    appHeader: Header,
    appExplanation: Explanation,
    appComment: Comment,
    appSpeaking: Speaking,
    appCommentGroup: CommentGroup,
    appLoading: Loading,
    appQualification: Qualification,
    appQualificationDialog: QualificationDialog,
    appStudentNote: StudentNote,
    HomeworkDetails,
    CreateHomeworkDialog,
    ProgressResetButton,
  },
  mixins: [directionMixin, studentMixin],
  data() {
    return {
      current: Number.parseInt(this.$route.query.sn) || 0,
      data: {},
      loading: true,
      answers: [],
      qualifications: [],
      qualification: {},
      dialog: false,
      commentDialog: false,
      loadingSave: false,
      homeworkDialog: false,
      createDialog: false,
      loadingAssign: false,
      homeworks: [],
    };
  },
  computed: {
    speak() {
      return this.data.speaks[this.current]?.uuid || "";
    },
    field() {
      return `${this.direction}_speak`;
    },
    fieldUuid() {
      return `${this.field}__uuid`;
    },
    filterFields() {
      return {
        [this.fieldUuid]: this.speak,
      };
    },
    createFields() {
      return {
        [this.field]: this.speak,
      };
    },
    disabled() {
      return this.loading || (!this.data.length && !this.isTeacher);
    },
    location() {
      if (this.direction === "practice") {
        return "PRACTICE";
      }
      return "LIVE";
    },
    profile() {
      return this.$store.getters.profile;
    },
  },
  methods: {
    async fetchHomeworks() {
      const params = {
        teacher_lesson__uuid: this.$store.getters["lesson/teacher_lesson"].uuid,
        location: this.location,
        students__student__email: this.student.email,
        ...this.filterFields,
      };
      if (!Object.keys(this.filterFields).length) {
        params.not_speak = true;
      }
      if (this.profile.role === "TEACHER") {
        params.teacher__email = this.profile.email;
      }
      const customs = await getHomeworks({
        ...params,
        type: "Custom",
      });
      this.homeworks = customs.data.results;
    },
    onDeleted(uuid) {
      const index = this.homeworks.findIndex((item) => item.uuid === uuid);
      if (index > -1) {
        this.homeworks.splice(index, 1);
      }
    },
    async onNewHomework(homework) {
      try {
        this.loadingAssign = true;
        await assignHomework({
          homework: homework.uuid,
          student: this.student.email,
        });
        this.createDialog = false;
        const newHomework = {
          ...homework,
          students: [
            {
              email: this.student.email,
              uuid: this.student.uuid,
            },
          ],
        };
        this.homeworks.push(newHomework);
      } finally {
        this.loadingAssign = false;
      }
    },
    onReset() {
      this.answers[this.current].uuid = undefined;
      this.answers[this.current].audio_file = undefined;
      this.qualification = {};
      this.$refs.commentGroup?.updateComments(this.data.speaks[this.current].comment_group);
    },
    async onSave() {
      try {
        this.loadingSave = true;
        if (this.$refs.speaking.audioBlob && !this.answers[this.current].uuid) {
          this.answers[this.current].audio_file = this.$refs.speaking.audioBlob;
          const response = await postAnswer(this.answers[this.current]);
          if (response) {
            this.answers.splice(this.current, 1, response.data);
          }
        }
        if (this.direction === "practice") {
          let qualification = await this.qualify();
          this.qualifications.splice(this.current, 1, qualification);
          this.qualification = qualification;
        }

        let word;
        if (this.direction === "teach") {
          word = "teach_progress";
        } else word = "practice_progress";

        let progress = this.$store.getters["progress/lesson"];

        const speak = progress[word].speaking_step.speaks.find(
          (element) => element.speak === this.data.speaks[this.current].uuid,
        );
        if (!speak.completed) {
          speak.completed = true;
          patchLessonProgress(progress).then((response) => {
            this.$store.commit("progress/lesson", response.data);
          });
        }
      } finally {
        this.loadingSave = false;
      }
    },
    async next() {
      this.$refs.speaking.removeAudio();

      this.nextStep();
    },
    backAction() {
      if (this.current > 0) {
        this.current--;
      } else {
        this.$emit("prev-step");
      }
    },
    nextStep() {
      let progress = this.$store.getters["progress/lesson"];

      let word;
      if (this.direction === "teach") {
        word = "teach_progress";
      } else word = "practice_progress";

      if (this.current === this.data.speaks.length - 1) {
        if (!progress[word].speaking_step.completed) {
          progress[word].speaking_step.completed = true;
          patchLessonProgress(progress).then((response) => {
            this.$store.commit("progress/lesson", response.data);
          });
        }
        this.$emit("next-step");
      } else {
        this.current += 1;
      }
    },
    onInput(e) {
      if (!e) {
        this.nextStep();
      }
    },
  },
  watch: {
    current(val) {
      this.$router.replace({
        query: { ...this.$route.query, sn: val },
      });
      this.qualification = this.qualifications[val];
      this.homeworks = [];
      this.fetchHomeworks();
    },
  },
  async created() {
    this.loading = true;
    const response = await getSpeaking(this.source.speaking_step, this.direction);
    this.data = response.data;

    for (let index = 0; index < this.data.speaks.length; index++) {
      const element = this.data.speaks[index];
      const response = await getAnswer({
        question__uuid: element.question_group.questions[0].uuid,
        student__email: this.student.email,
      });
      if (response.data.count) {
        this.answers.push(response.data.results[0]);
      } else
        this.answers.push({
          question: element.question_group.questions[0].uuid,
          student: this.student.email,
        });

      const qualification = await this.fetchQualification(
        this.student.email,
        element.question_group.uuid,
      );
      this.qualifications.push(qualification);
    }
    if (this.qualifications[0]) {
      this.qualification = this.qualifications[0];
    }
    await this.fetchHomeworks();

    this.loading = false;
  },
};
</script>

<style scoped>
.comment__div {
  border: dashed #29284c 2px !important;
  border-radius: 10px;
  padding-right: 20px !important;
  padding-left: 20px !important;
}
</style>
